// ============================================================================
// Motor de endosos: aplica el CAMBIO ESTRUCTURAL de un endoso a la póliza y
// guarda el "antes" (snapshot) para poder reversarlo. La PRIMA la maneja el
// caller (dilución en cuotas / clawback) — este motor solo toca los campos.
//
// Cada endoso declara un `target` (viene del catálogo catalogo_endoso_tipos.target)
// que define qué campo(s) toca. Es fleet-aware: los endosos de vehículo operan
// sobre un vehículo específico (poliza_autos.id).
// ============================================================================
import { supabase, insertRow, updateRow } from './engine';
import { reaplicarSacoPoliza } from './saco';
import { generarComisionNegativa } from './comisiones';

// Campos del vehículo (poliza_autos) que un endoso puede setear.
const VEH_FIELDS = ['anio', 'marca', 'modelo', 'color', 'tipo_vehiculo', 'pasajeros',
  'no_motor', 'chasis', 'chasis_igual_motor', 'placa', 'placa_por_reportar',
  'dueno_nombre', 'dueno_cedula'];
// Campos "generales" de la póliza editables por endoso de datos.
const GEN_FIELDS = ['descripcion_riesgo', 'beneficiario_nombre', 'beneficiario_cedula',
  'asegurado_nombre', 'asegurado_cedula', 'tomador_nombre', 'tomador_cedula'];
// Campos de FIANZA (monto afianzado / tarifa / beneficiario de la fianza).
const FIANZA_FIELDS = ['monto_afianzado', 'tarifa_fianza', 'beneficiario_fianza'];

const pick = (obj, keys) => { const o = {}; for (const k of keys) o[k] = obj?.[k]; return o; };

// Arma el payload de poliza_autos a partir de los `cambios` capturados.
function vehiculoPayload(c) {
  const p = {};
  for (const k of VEH_FIELDS) if (c[k] !== undefined && c[k] !== '') p[k] = c[k];
  if (p.anio !== undefined) p.anio = p.anio ? parseInt(p.anio) : null;
  if (p.pasajeros !== undefined) p.pasajeros = p.pasajeros ? parseInt(p.pasajeros) : null;
  // Placa: "Por reportar" cuando así se marca.
  if (c.placa_por_reportar) { p.placa = 'Por reportar'; p.placa_por_reportar = true; }
  // Si chasis = motor, se copia.
  if (c.chasis_igual_motor && p.no_motor) p.chasis = p.no_motor;
  // Dueño: si es el contratante, no se guarda propietario; si no, se guardan sus datos.
  if (c.dueno_igual) { p.dueno_nombre = null; p.dueno_cedula = null; }
  else if (c.dueno_igual === false) { p.dueno_nombre = c.dueno_nombre || null; p.dueno_cedula = c.dueno_cedula || null; }
  return p;
}

// ¿Este target hace un cambio estructural que este motor sabe aplicar?
export function targetAplica(target) {
  return ['vehiculo_inclusion', 'vehiculo_exclusion', 'vehiculo_cambio', 'vehiculo_datos',
    'suma_cambio', 'deducible_cambio', 'cobertura_inclusion', 'cobertura_exclusion',
    'forma_pago', 'fecha_pago', 'vigencia', 'datos_generales', 'fianza_datos',
    'ubicacion_inclusion', 'ubicacion_exclusion',
    'equipo_inclusion', 'equipo_exclusion',
    'asegurado_inclusion', 'asegurado_exclusion'].includes(target);
}

// Re-fecha un ISO 'YYYY-MM-DD' al `dia` indicado del MISMO mes/año, recortando al
// último día del mes cuando el día pedido no existe (ej. 31 en febrero → 28/29).
function reFechaDia(iso, dia) {
  if (!iso) return iso;
  const [y, m] = iso.split('-').map(Number);
  const ultimo = new Date(y, m, 0).getDate(); // m es 1-based: día 0 del "mes m" = último día del mes m
  const d = Math.min(Math.max(1, parseInt(dia) || 1), ultimo);
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// Tipo de unidad (poliza_unidades.tipo) según el target del endoso.
// ubicacion (Incendio) | equipo (Ingeniería) | asegurado (colectivos).
function tipoUnidad(target) {
  if (target && target.startsWith('ubicacion_')) return 'ubicacion';
  if (target && target.startsWith('equipo_')) return 'equipo';
  if (target && target.startsWith('asegurado_')) return 'asegurado';
  return null;
}

// Aplica el cambio y devuelve el snapshot (el "antes") para poder reversarlo.
// `target` string, `cambios` objeto capturado en el formulario del endoso.
export async function aplicarCambioEndoso(polizaId, target, cambios) {
  const c = cambios || {};
  switch (target) {
    case 'vehiculo_inclusion': {
      const row = await insertRow('poliza_autos', { poliza_id: polizaId, ...vehiculoPayload(c) });
      // Coberturas de la unidad incluida (ligadas al vehículo por poliza_auto_id).
      const coberturas_agregadas = [];
      for (const cob of (c.coberturas || [])) {
        if (!cob.nombre) continue;
        const cr = await insertRow('poliza_coberturas', {
          poliza_id: polizaId, poliza_auto_id: row?.id || null,
          nombre: cob.nombre, tipo_limite: cob.tipo_limite || 'limite',
          suma_asegurada: cob.suma_asegurada !== '' && cob.suma_asegurada != null ? parseFloat(cob.suma_asegurada) : null,
          deducible: cob.deducible || null,
        });
        if (cr?.id) coberturas_agregadas.push(cr.id);
      }
      return { target, vehiculo_id_agregado: row?.id || null, coberturas_agregadas };
    }
    case 'vehiculo_exclusion': {
      const id = parseInt(c.vehiculo_id);
      const { data: before } = await supabase.from('poliza_autos').select('*').eq('id', id).maybeSingle();
      // Al excluir la unidad se quitan también sus coberturas (para reponerlas al reversar).
      const { data: beforeCobs } = await supabase.from('poliza_coberturas').select('*').eq('poliza_auto_id', id);
      if (beforeCobs?.length) await supabase.from('poliza_coberturas').delete().eq('poliza_auto_id', id);
      if (before) await supabase.from('poliza_autos').delete().eq('id', id);
      return { target, vehiculo_excluido: before || null, coberturas_excluidas: beforeCobs || [] };
    }
    case 'vehiculo_cambio': {
      const id = parseInt(c.vehiculo_id);
      const { data: before } = await supabase.from('poliza_autos').select('*').eq('id', id).maybeSingle();
      await updateRow('poliza_autos', id, vehiculoPayload(c));
      return { target, vehiculo_id: id, antes: before || null };
    }
    case 'vehiculo_datos': {
      const id = parseInt(c.vehiculo_id);
      const payload = vehiculoPayload(c);
      const { data: before } = await supabase.from('poliza_autos').select('*').eq('id', id).maybeSingle();
      if (Object.keys(payload).length) await updateRow('poliza_autos', id, payload);
      return { target, vehiculo_id: id, antes: before ? pick(before, Object.keys(payload)) : null };
    }
    case 'suma_cambio': {
      const id = parseInt(c.cobertura_id); // poliza_coberturas.id
      const { data: before } = await supabase.from('poliza_coberturas').select('*').eq('id', id).maybeSingle();
      await updateRow('poliza_coberturas', id, {
        suma_asegurada: c.suma_asegurada !== '' && c.suma_asegurada != null ? parseFloat(c.suma_asegurada) : null,
      });
      return { target, cobertura_id: id, antes: before ? { suma_asegurada: before.suma_asegurada } : null };
    }
    case 'deducible_cambio': {
      const id = parseInt(c.cobertura_id); // poliza_coberturas.id
      const { data: before } = await supabase.from('poliza_coberturas').select('*').eq('id', id).maybeSingle();
      await updateRow('poliza_coberturas', id, { deducible: c.deducible || null });
      return { target, cobertura_id: id, antes: before ? { deducible: before.deducible } : null };
    }
    case 'cobertura_inclusion': {
      const row = await insertRow('poliza_coberturas', {
        poliza_id: polizaId,
        cobertura_id: c.cobertura_catalogo_id ? parseInt(c.cobertura_catalogo_id) : null,
        nombre: c.nombre || '',
        tipo_limite: c.tipo_limite || 'limite',
        suma_asegurada: c.suma_asegurada !== '' && c.suma_asegurada != null ? parseFloat(c.suma_asegurada) : null,
        deducible: c.deducible || null,
        otros: c.otros || null,
      });
      return { target, cobertura_id_agregada: row?.id || null };
    }
    case 'cobertura_exclusion': {
      const id = parseInt(c.cobertura_id);
      const { data: before } = await supabase.from('poliza_coberturas').select('*').eq('id', id).maybeSingle();
      if (before) await supabase.from('poliza_coberturas').delete().eq('id', id);
      return { target, cobertura_excluida: before || null };
    }
    case 'forma_pago': {
      const { data: before } = await supabase.from('polizas').select('forma_pago, frecuencia_pago').eq('id', polizaId).maybeSingle();
      const patch = {};
      if (c.forma_pago !== undefined) patch.forma_pago = c.forma_pago || null;
      if (c.frecuencia_pago) patch.frecuencia_pago = c.frecuencia_pago;
      if (Object.keys(patch).length) await updateRow('polizas', polizaId, patch);
      return { target, antes: before || null };
    }
    case 'fecha_pago': {
      // Cambio del DÍA de pago: re-fecha las cuotas del plan (cuota_num != null) que aún
      // NO están pagadas al día acordado (mismo mes/año), sin tocar prima ni montos.
      // Las cuotas ya pagadas quedan como están (su vencimiento es histórico). Reaplica
      // el saco al final porque cambió el orden de vencimientos.
      const dia = parseInt(c.dia_pago);
      if (!dia || dia < 1 || dia > 31) return { target, antes: [], noop: true };
      const { data: cuotas } = await supabase.from('pagos')
        .select('id, fecha_vencimiento, estado, cuota_num')
        .eq('poliza_id', polizaId);
      const objetivo = (cuotas || []).filter(p =>
        p.cuota_num != null && p.estado !== 'pagado' && p.fecha_vencimiento);
      const antes = [];
      for (const p of objetivo) {
        const nueva = reFechaDia(p.fecha_vencimiento, dia);
        if (nueva === p.fecha_vencimiento) continue;
        antes.push({ id: p.id, fecha_vencimiento: p.fecha_vencimiento });
        await updateRow('pagos', p.id, { fecha_vencimiento: nueva });
      }
      if (antes.length) await reaplicarSacoPoliza(polizaId);
      return { target, dia_pago: dia, antes };
    }
    case 'vigencia': {
      const { data: before } = await supabase.from('polizas').select('fecha_inicio, fecha_vencimiento').eq('id', polizaId).maybeSingle();
      const patch = {};
      if (c.fecha_inicio) patch.fecha_inicio = c.fecha_inicio;
      if (c.fecha_vencimiento) patch.fecha_vencimiento = c.fecha_vencimiento;
      if (Object.keys(patch).length) await updateRow('polizas', polizaId, patch);
      return { target, antes: before || null };
    }
    case 'datos_generales': {
      const patch = {};
      for (const k of GEN_FIELDS) if (c[k] !== undefined) patch[k] = c[k] || null;
      const { data: before } = await supabase.from('polizas').select(GEN_FIELDS.join(',')).eq('id', polizaId).maybeSingle();
      if (Object.keys(patch).length) await updateRow('polizas', polizaId, patch);
      // Si el endoso tocó el beneficiario, sella la fecha de última actualización.
      // (dato visible para todos; el DETALLE del beneficiario es confidencial — ver
      // src/components/BeneficiarioConfidencial y AppContext.puedeVerBeneficiarios)
      if ('beneficiario_nombre' in patch || 'beneficiario_cedula' in patch)
        await updateRow('polizas', polizaId, { beneficiarios_actualizado_el: new Date().toISOString().slice(0, 10) });
      return { target, antes: before ? pick(before, Object.keys(patch)) : null };
    }
    case 'fianza_datos': {
      const patch = {};
      for (const k of FIANZA_FIELDS) {
        if (c[k] === undefined) continue;
        if (k === 'beneficiario_fianza') patch[k] = c[k] || null;
        else patch[k] = (c[k] !== '' && c[k] != null) ? parseFloat(c[k]) : null;
      }
      const { data: before } = await supabase.from('polizas').select(FIANZA_FIELDS.join(',')).eq('id', polizaId).maybeSingle();
      if (Object.keys(patch).length) await updateRow('polizas', polizaId, patch);
      return { target, antes: before ? pick(before, Object.keys(patch)) : null };
    }
    case 'ubicacion_inclusion':
    case 'equipo_inclusion':
    case 'asegurado_inclusion': {
      const tipo = tipoUnidad(target);
      // Una inclusión puede traer VARIAS unidades (p. ej. varios asegurados).
      const items = (c.unidades && c.unidades.length) ? c.unidades : [c];
      const unidades_agregadas = [], coberturas_agregadas = [];
      for (const u of items) {
        if (!u || (!u.nombre && !u.identificacion)) continue;
        const row = await insertRow('poliza_unidades', {
          poliza_id: polizaId, tipo,
          // Asegurados capturan Nombre + Apellido por separado; se guardan combinados.
          nombre: [u.nombre, u.apellido].filter(Boolean).join(' ') || null,
          identificacion: u.identificacion || null,
          valor: (u.valor !== '' && u.valor != null) ? parseFloat(u.valor) : null,
          datos: u.datos || null,
        });
        if (row?.id) unidades_agregadas.push(row.id);
        for (const cob of (u.coberturas || [])) {
          if (!cob.nombre) continue;
          const cr = await insertRow('poliza_coberturas', {
            poliza_id: polizaId, poliza_unidad_id: row?.id || null,
            nombre: cob.nombre, tipo_limite: cob.tipo_limite || 'limite',
            suma_asegurada: (cob.suma_asegurada !== '' && cob.suma_asegurada != null) ? parseFloat(cob.suma_asegurada) : null,
            deducible: cob.deducible || null,
          });
          if (cr?.id) coberturas_agregadas.push(cr.id);
        }
        // Dependientes ligados a este asegurado (colectivos Vida/Salud): se guardan como
        // unidades tipo 'dependiente' con parent_id = el asegurado titular. El parentesco va en `datos`.
        if (tipo === 'asegurado' && row?.id) {
          for (const dep of (u.dependientes || [])) {
            if (!dep || (!dep.nombre && !dep.identificacion)) continue;
            const dr = await insertRow('poliza_unidades', {
              poliza_id: polizaId, tipo: 'dependiente', parent_id: row.id,
              nombre: [dep.nombre, dep.apellido].filter(Boolean).join(' ') || null, identificacion: dep.identificacion || null,
              valor: (dep.valor !== '' && dep.valor != null) ? parseFloat(dep.valor) : null,
              datos: dep.parentesco ? { parentesco: dep.parentesco } : null,
            });
            if (dr?.id) unidades_agregadas.push(dr.id);
          }
        }
      }
      return { target, unidades_agregadas, coberturas_agregadas };
    }
    case 'ubicacion_exclusion':
    case 'equipo_exclusion':
    case 'asegurado_exclusion': {
      const id = parseInt(c.unidad_id);
      const { data: before } = await supabase.from('poliza_unidades').select('*').eq('id', id).maybeSingle();
      const { data: beforeCobs } = await supabase.from('poliza_coberturas').select('*').eq('poliza_unidad_id', id);
      if (beforeCobs?.length) await supabase.from('poliza_coberturas').delete().eq('poliza_unidad_id', id);
      if (before) await supabase.from('poliza_unidades').delete().eq('id', id);
      return { target, unidad_excluida: before || null, coberturas_excluidas: beforeCobs || [] };
    }
    default:
      // 'libre', 'cancelacion', 'prorroga' → sin cambio estructural aquí
      // (cancelación/prórroga las maneja el caller como hasta ahora).
      return { target: target || 'libre', noop: true };
  }
}

// Aplica el MOVIMIENTO DE PRIMA de un endoso, con la MISMA mecánica del endoso normal:
// diluye el diferencial (con impuesto) entre las cuotas pendientes cuyo vencimiento cae
// desde `fechaDesde` (registrando cada ajuste en poliza_endoso_ajustes para poder
// revertirlo); si el delta es negativo, registra una nota de crédito + comisión negativa;
// si no hay cuotas en rango, crea una letra suelta. Reaplica el saco al final.
// (No cubre declarativas — Auto no es declarativa. Para eso se usa el flujo de Solicitudes.)
export async function aplicarPrimaEndoso(polizaId, endosoId, deltaPrima, { impuesto = 5, fechaDesde, fechaHasta = null, formaPago = null, recurrenteMensual = false } = {}) {
  const delta = parseFloat(deltaPrima) || 0;
  if (!delta || !endosoId) return;
  const imp = parseFloat(impuesto);
  const total = Math.round(delta * (1 + (isNaN(imp) ? 5 : imp) / 100) * 100) / 100;
  const desde = fechaDesde || new Date().toISOString().slice(0, 10);

  if (recurrenteMensual) {
    // Incremento MENSUAL recurrente: suma el total (con imp.) a CADA cuota futura pendiente
    // (venc. >= desde), no lo diluye. Registra cada suma en poliza_endoso_ajustes (reversible).
    const { data: rango } = await supabase.from('pagos')
      .select('id, monto, cuota_num, fecha_vencimiento')
      .eq('poliza_id', polizaId).gte('fecha_vencimiento', desde).order('fecha_vencimiento');
    const futuras = (rango || []).filter(p => p.cuota_num != null);
    if (futuras.length) {
      for (const cu of futuras) {
        const nuevoMonto = Math.round(((parseFloat(cu.monto) || 0) + total) * 100) / 100;
        await updateRow('pagos', cu.id, { monto: nuevoMonto });
        await insertRow('poliza_endoso_ajustes', { endoso_id: endosoId, pago_id: cu.id, monto: total });
      }
    } else {
      await insertRow('pagos', {
        poliza_id: polizaId, monto: total, estado: 'pendiente',
        fecha_vencimiento: desde, metodo: formaPago || null,
        cuota_num: null, origen: 'endoso', endoso_id: endosoId,
      });
    }
  } else if (total < 0) {
    // Reducción / devolución: nota de crédito + comisión negativa (clawback).
    const credito = await insertRow('pagos', {
      poliza_id: polizaId, monto: total, estado: 'pendiente',
      fecha_vencimiento: desde, metodo: formaPago || null,
      cuota_num: null, origen: 'endoso', endoso_id: endosoId,
    });
    if (credito?.id) await generarComisionNegativa(polizaId, credito.id, total);
  } else {
    // Aumento: diluir entre las cuotas en rango; si no hay, letra suelta.
    const { data: rango } = await supabase.from('pagos')
      .select('id, monto, cuota_num, fecha_vencimiento')
      .eq('poliza_id', polizaId).gte('fecha_vencimiento', desde).order('fecha_vencimiento');
    const enRango = (rango || []).filter(p =>
      p.cuota_num != null && (!fechaHasta || (p.fecha_vencimiento && p.fecha_vencimiento <= fechaHasta)));
    if (enRango.length > 0) {
      const n = enRango.length;
      const base = Math.floor(total / n * 100) / 100;
      for (let i = 0; i < n; i++) {
        const share = i === n - 1 ? Math.round((total - base * (n - 1)) * 100) / 100 : base;
        const nuevoMonto = Math.round(((parseFloat(enRango[i].monto) || 0) + share) * 100) / 100;
        await updateRow('pagos', enRango[i].id, { monto: nuevoMonto });
        await insertRow('poliza_endoso_ajustes', { endoso_id: endosoId, pago_id: enRango[i].id, monto: share });
      }
    } else {
      await insertRow('pagos', {
        poliza_id: polizaId, monto: total, estado: 'pendiente',
        fecha_vencimiento: fechaHasta || desde, metodo: formaPago || null,
        cuota_num: null, origen: 'endoso', endoso_id: endosoId,
      });
    }
  }
  await reaplicarSacoPoliza(polizaId);
}

// Revierte el cambio usando el snapshot generado por aplicarCambioEndoso.
// `polizaId` se necesita para revertir cambios sobre la propia póliza
// (forma de pago, vigencia, datos generales).
export async function reversarCambioEndoso(polizaId, snapshot) {
  if (!snapshot || snapshot.noop) return;
  switch (snapshot.target) {
    case 'vehiculo_inclusion':
      // Quitar primero las coberturas que agregó la inclusión, luego el vehículo.
      if (snapshot.coberturas_agregadas?.length)
        await supabase.from('poliza_coberturas').delete().in('id', snapshot.coberturas_agregadas);
      if (snapshot.vehiculo_id_agregado)
        await supabase.from('poliza_autos').delete().eq('id', snapshot.vehiculo_id_agregado);
      break;
    case 'vehiculo_exclusion':
      if (snapshot.vehiculo_excluido) {
        const { id, ...rest } = snapshot.vehiculo_excluido; // reinsertar con id NUEVO
        const nuevo = await insertRow('poliza_autos', rest);
        // Reponer las coberturas de la unidad, ligadas al vehículo reinsertado.
        for (const cob of (snapshot.coberturas_excluidas || [])) {
          const { id: cid, poliza_auto_id, ...crest } = cob;
          await insertRow('poliza_coberturas', { ...crest, poliza_auto_id: nuevo?.id || null });
        }
      }
      break;
    case 'vehiculo_cambio':
      if (snapshot.vehiculo_id && snapshot.antes) {
        const { id, poliza_id, ...rest } = snapshot.antes;
        await updateRow('poliza_autos', snapshot.vehiculo_id, rest);
      }
      break;
    case 'vehiculo_datos':
      if (snapshot.vehiculo_id && snapshot.antes)
        await updateRow('poliza_autos', snapshot.vehiculo_id, snapshot.antes);
      break;
    case 'suma_cambio':
      if (snapshot.cobertura_id && snapshot.antes)
        await updateRow('poliza_coberturas', snapshot.cobertura_id, { suma_asegurada: snapshot.antes.suma_asegurada });
      break;
    case 'deducible_cambio':
      if (snapshot.cobertura_id && snapshot.antes)
        await updateRow('poliza_coberturas', snapshot.cobertura_id, { deducible: snapshot.antes.deducible });
      break;
    case 'cobertura_inclusion':
      if (snapshot.cobertura_id_agregada)
        await supabase.from('poliza_coberturas').delete().eq('id', snapshot.cobertura_id_agregada);
      break;
    case 'cobertura_exclusion':
      if (snapshot.cobertura_excluida) {
        const { id, ...rest } = snapshot.cobertura_excluida;
        await insertRow('poliza_coberturas', rest);
      }
      break;
    case 'fecha_pago':
      // Restaurar las fechas de vencimiento originales de cada cuota re-fechada.
      if (Array.isArray(snapshot.antes) && snapshot.antes.length) {
        for (const a of snapshot.antes)
          await updateRow('pagos', a.id, { fecha_vencimiento: a.fecha_vencimiento });
        if (polizaId) await reaplicarSacoPoliza(polizaId);
      }
      break;
    case 'forma_pago':
    case 'vigencia':
    case 'datos_generales':
    case 'fianza_datos':
      if (snapshot.antes && polizaId) await updateRow('polizas', polizaId, snapshot.antes);
      break;
    case 'ubicacion_inclusion':
    case 'equipo_inclusion':
    case 'asegurado_inclusion':
      // Quitar primero las coberturas agregadas, luego las unidades.
      if (snapshot.coberturas_agregadas?.length)
        await supabase.from('poliza_coberturas').delete().in('id', snapshot.coberturas_agregadas);
      if (snapshot.unidades_agregadas?.length)
        await supabase.from('poliza_unidades').delete().in('id', snapshot.unidades_agregadas);
      break;
    case 'ubicacion_exclusion':
    case 'equipo_exclusion':
    case 'asegurado_exclusion':
      if (snapshot.unidad_excluida) {
        const { id, ...rest } = snapshot.unidad_excluida; // reinsertar con id NUEVO
        const nuevo = await insertRow('poliza_unidades', rest);
        for (const cob of (snapshot.coberturas_excluidas || [])) {
          const { id: cid, poliza_unidad_id, ...crest } = cob;
          await insertRow('poliza_coberturas', { ...crest, poliza_unidad_id: nuevo?.id || null });
        }
      }
      break;
    default:
      break;
  }
}
